import { useEffect } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { AppShell } from '../../components/layout/AppShell';
import { PageBody } from '../../components/layout/PageBody';
import { LoadingState } from '../../components/common/LoadingState';
import { useFeedback } from '../../components/common/FeedbackProvider';
import { useAuth } from '../../hooks/useAuth';
import {
  canComposeAlerts,
  canManageSources,
  canManageUsers,
  canViewAnalytics,
} from '../../utils/roles';
import { SHOW_NEWS_AGENT, SHOW_NEWS_SOURCES } from '../../config/app';
import { AlertsTab } from './AlertsTab';
import { AnalyticsTab } from './AnalyticsTab';
import { ContentTab } from './ContentTab';
import { DashboardTab } from './DashboardTab';
import { IntakeTab } from './IntakeTab';
import { SourcesTab } from './SourcesTab';
import { UsersTab } from './UsersTab';

function tabMeta(tab, role) {
  if (tab === 'dashboard') {
    return {
      title: 'Dashboard',
      subtitle: SHOW_NEWS_SOURCES
        ? 'Your drafts, reviews, live stories, and news sources'
        : 'Your drafts, reviews, and live stories',
    };
  }
  if (tab === 'content') {
    return {
      title: 'Content',
      subtitle:
        role === 'maker'
          ? 'Your drafts and live posts'
          : role === 'checker'
            ? 'Stories waiting for review, plus anything you wrote'
            : 'Drafts, reviews, and live stories you can publish',
    };
  }
  if (tab === 'sources') {
    return {
      title: 'News sources',
      subtitle: 'Connect websites and import their latest stories',
    };
  }
  if (tab === 'intake') {
    return {
      title: 'Scrape',
      subtitle: 'Collect stories from the news engine as drafts',
    };
  }
  if (tab === 'analytics') {
    return {
      title: 'Analytics',
      subtitle: 'Story views, likes, and comments',
    };
  }
  if (tab === 'users') {
    return {
      title: 'Users',
      subtitle: 'Manage accounts, roles, and access',
    };
  }
  if (tab === 'alerts') {
    return {
      title: 'Compose alerts',
      subtitle: 'Send a message to signed-in people',
    };
  }
  return null;
}

function StaffTabRedirect({ to = '/admin', message }) {
  const navigate = useNavigate();
  const { showError } = useFeedback();
  useEffect(() => {
    if (message) showError(message);
    navigate(to, { replace: true });
  }, [message, navigate, showError, to]);
  return <LoadingState label="Opening a page you can use…" />;
}

export function AdminPage() {
  const { tab: tabParam } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const tab = tabParam || 'dashboard';

  const meta = tabMeta(tab, user?.role);
  if (!meta) {
    return <Navigate to="/admin" replace />;
  }

  if (tab === 'alerts' && !canComposeAlerts(user?.role)) {
    return (
      <StaffTabRedirect message="Your role cannot send alerts." />
    );
  }

  if (tab === 'users' && !canManageUsers(user?.role)) {
    return (
      <StaffTabRedirect message="Only an admin can manage users." />
    );
  }

  if (tab === 'analytics' && !canViewAnalytics(user?.role)) {
    return (
      <StaffTabRedirect message="Only an admin can open Analytics." />
    );
  }

  if (tab === 'sources' && !SHOW_NEWS_SOURCES) {
    return <Navigate to="/admin" replace />;
  }

  if (tab === 'intake' && !SHOW_NEWS_AGENT) {
    return <Navigate to="/admin" replace />;
  }

  if (tab === 'intake' && !canManageSources(user?.role)) {
    return (
      <StaffTabRedirect message="Your role cannot scrape news into drafts." />
    );
  }

  function goTab(next, extra = {}) {
    const params = new URLSearchParams();
    if (extra.status) params.set('status', extra.status);
    const qs = params.toString();
    const path = next === 'dashboard' ? '/admin' : `/admin/${next}`;
    navigate(qs ? `${path}?${qs}` : path, { replace: true });
  }

  return (
    <AppShell title={meta.title} subtitle={meta.subtitle} maxWidth="md">
      <PageBody>
        {tab === 'dashboard' && <DashboardTab onGoTab={goTab} />}
        {tab === 'content' && <ContentTab />}
        {tab === 'intake' && SHOW_NEWS_AGENT && (
          <IntakeTab onGoTab={goTab} />
        )}
        {tab === 'sources' && SHOW_NEWS_SOURCES && <SourcesTab />}
        {tab === 'analytics' && <AnalyticsTab />}
        {tab === 'users' && <UsersTab />}
        {tab === 'alerts' && <AlertsTab />}
      </PageBody>
    </AppShell>
  );
}
